import type { TrailerTypeRecord } from "@/hooks/useTrailerTypes";

/**
 * Tenants where Bobtail missions DO create external (EAPI) trips.
 * Everywhere else bobtail remains a no-EAPI state.
 */
export const BOBTAIL_EAPI_TENANTS = ["dev", "staging"] as const;

/** Fail-closed: unknown/missing tenant => bobtail skips EAPI. */
export function isBobtailEapiEnabled(tenantSlug?: string | null): boolean {
  return !!tenantSlug && (BOBTAIL_EAPI_TENANTS as readonly string[]).includes(tenantSlug);
}

/**
 * Determines whether an order should skip Aurora EAPI integration.
 * "Manual" trip types are never supported by the Aurora Driver.
 * Bobtail trailer types skip EAPI except on tenants in `BOBTAIL_EAPI_TENANTS`.
 */
export function shouldSkipEapi(
  trailerTypeId: string | null | undefined,
  tripType: string | null | undefined,
  trailerTypes: TrailerTypeRecord[] | undefined,
  tenantSlug?: string | null,
): boolean {
  if (tripType === "Manual") return true;
  if (!trailerTypeId || !trailerTypes) return false;
  const tt = trailerTypes.find((t) => t.id === trailerTypeId);
  if (tt?.kind !== "bobtail") return false;
  return !isBobtailEapiEnabled(tenantSlug);
}

/**
 * Server-side variant: looks up the trailer type kind from the DB.
 * Returns true if the order's effective state should skip EAPI.
 */
export function isNoEapiState(
  trailerTypeKind: string | null | undefined,
  tripType: string | null | undefined,
  tenantSlug?: string | null,
): boolean {
  if (tripType === "Manual") return true;
  if (trailerTypeKind !== "bobtail") return false;
  return !isBobtailEapiEnabled(tenantSlug);
}
